import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import bbhLogoUrl from '@/assets/bbh-logo-on-dark.svg';
import './MobileNav.css';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/people', label: 'People', icon: '👥' },
    { path: '/companies', label: 'Companies', icon: '🏢' },
    { path: '/projects', label: 'Projects', icon: '📁' },
    { path: '/ew-tools', label: 'EW Tools', icon: '🛠️' },
    { path: '/docs', label: 'Docs', icon: '📖' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="mobile-nav-backdrop" onClick={onClose} />

      {/* Drawer */}
      <nav className="mobile-nav">
        <div className="mobile-nav-header">
          <img className="mobile-nav-logo" src={bbhLogoUrl} alt="Backed by Humans" />
          <button className="mobile-nav-close" onClick={onClose} aria-label="Close menu">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <ul className="mobile-nav-list">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={onClose}
              >
                <span className="mobile-nav-icon">{item.icon}</span>
                <span className="mobile-nav-label">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="mobile-nav-footer">
          {user && (
            <div className="mobile-nav-user">
              <span className="mobile-nav-user-name">{user.full_name}</span>
              <span className="mobile-nav-user-email">{user.email}</span>
            </div>
          )}
          <button
            className="mobile-nav-logout"
            onClick={async () => {
              onClose();
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            <span className="mobile-nav-icon">🚪</span>
            <span className="mobile-nav-label">Logout</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
